import React from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import AddChoiceForm from './AddChoiceForm';

interface PricingFormState {
  basePrice: number;
  options: any[];
}

interface PricingModalProps {
  show: boolean;
  onHide: () => void;
  selectedProduct: any;
  pricingForm: PricingFormState;
  setPricingForm: React.Dispatch<React.SetStateAction<PricingFormState>>;
  pricingError: string;
  onSavePricing: (e: React.FormEvent) => Promise<void>;
  onUpdateOptionChoiceCost: (optIndex: number, choiceIndex: number, newCost: number) => void;
  onAddOptionChoice: (optIndex: number, label: string, extraCost: number) => void;
  onDeleteOptionChoice: (optIndex: number, choiceIndex: number) => void;
}

const PricingModal: React.FC<PricingModalProps> = ({
  show,
  onHide,
  selectedProduct,
  pricingForm,
  setPricingForm,
  pricingError,
  onSavePricing,
  onUpdateOptionChoiceCost,
  onAddOptionChoice,
  onDeleteOptionChoice,
}) => {
  if (!selectedProduct) {
    return null;
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold text-dark d-flex align-items-center gap-2">
          <i className={selectedProduct.icon}></i> Manage Pricing - {selectedProduct.name}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={onSavePricing}>
        <Modal.Body className="p-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {pricingError && (
            <Alert variant="danger" className="border-0 py-2 rounded-3 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '12.5px' }}>
              <i className="fas fa-exclamation-triangle"></i>
              <div>{pricingError}</div>
            </Alert>
          )}

          <div className="bg-light p-3 rounded-4 border mb-4">
            <Form.Group controlId="formProductBasePrice">
              <Form.Label className="fw-bold text-dark mb-1" style={{ fontSize: '14px' }}>
                Base Price (INR per unit)
              </Form.Label>
              <p className="text-muted mb-2" style={{ fontSize: '11px' }}>
                The starting cost before any customizable configuration options are added.
              </p>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                placeholder="Enter base price"
                value={pricingForm.basePrice}
                onChange={(e) => setPricingForm(prev => ({ ...prev, basePrice: parseFloat(e.target.value) || 0 }))}
                style={{ borderRadius: '8px', fontSize: '14px', maxWidth: '200px' }}
                required
              />
            </Form.Group>
          </div>

          <h5 className="fw-bold text-dark mb-3 border-bottom pb-2">Customizable Options Markups</h5>
          {pricingForm.options.length === 0 ? (
            <p className="text-muted italic small">No custom options for this product.</p>
          ) : (
            pricingForm.options.map((opt: any, optIdx: number) => {
              if (opt.type === 'number') {
                return (
                  <div key={optIdx} className="border rounded-4 p-3 mb-3 bg-white shadow-sm">
                    <div className="fw-bold text-dark mb-2" style={{ fontSize: '13px' }}>
                      <i className="fas fa-sliders-h me-2 text-muted"></i>{opt.label} <span className="text-muted font-monospace fw-normal" style={{ fontSize: '11px' }}>(Numeric Option)</span>
                    </div>
                    <Row className="g-2">
                      <Col xs={4}>
                        <Form.Label className="text-muted" style={{ fontSize: '11.5px' }}>Min Value</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          value={opt.min || ''}
                          onChange={(e) => setPricingForm(prev => {
                            const copy = [...prev.options];
                            copy[optIdx].min = e.target.value === '' ? null : Number(e.target.value);
                            return { ...prev, options: copy };
                          })}
                          style={{ borderRadius: '6px' }}
                        />
                      </Col>
                      <Col xs={4}>
                        <Form.Label className="text-muted" style={{ fontSize: '11.5px' }}>Max Value</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          value={opt.max || ''}
                          onChange={(e) => setPricingForm(prev => {
                            const copy = [...prev.options];
                            copy[optIdx].max = e.target.value === '' ? null : Number(e.target.value);
                            return { ...prev, options: copy };
                          })}
                          style={{ borderRadius: '6px' }}
                        />
                      </Col>
                      <Col xs={4}>
                        <Form.Label className="text-muted" style={{ fontSize: '11.5px' }}>Unit</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={opt.unit || ''}
                          onChange={(e) => setPricingForm(prev => {
                            const copy = [...prev.options];
                            copy[optIdx].unit = e.target.value;
                            return { ...prev, options: copy };
                          })}
                          style={{ borderRadius: '6px' }}
                        />
                      </Col>
                    </Row>
                  </div>
                );
              }

              return (
                <div key={optIdx} className="border rounded-4 p-3 mb-3 bg-white shadow-sm">
                  <div className="fw-bold text-dark border-bottom pb-2 mb-2 d-flex justify-content-between align-items-center" style={{ fontSize: '13.5px' }}>
                    <span>
                      <i className="fas fa-list-ul me-2 text-muted"></i>{opt.label} <span className="text-muted font-monospace" style={{ fontSize: '11px', fontWeight: 'normal' }}>( {opt.type} )</span>
                    </span>
                  </div>

                  <div className="d-flex flex-column gap-2 mb-3">
                    {opt.choices && opt.choices.map((choice: any, choiceIdx: number) => (
                      <div key={choiceIdx} className="d-flex align-items-center gap-2 bg-light p-2 rounded-3">
                        <span className="fw-medium text-dark flex-grow-1" style={{ fontSize: '12.5px' }}>{choice.label}</span>
                        <div className="d-flex align-items-center gap-1.5" style={{ width: '130px' }}>
                          <span style={{ fontSize: '12px' }} className="text-muted">₹</span>
                          <Form.Control
                            type="number"
                            step="0.01"
                            size="sm"
                            placeholder="Extra cost"
                            value={choice.extraCost}
                            onChange={(e) => onUpdateOptionChoiceCost(optIdx, choiceIdx, parseFloat(e.target.value) || 0)}
                            style={{ borderRadius: '6px', fontSize: '12px' }}
                          />
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="px-2 py-0.5 border-0"
                          onClick={() => onDeleteOptionChoice(optIdx, choiceIdx)}
                          title="Delete Choice"
                        >
                          <i className="fas fa-times-circle"></i>
                        </Button>
                      </div>
                    ))}
                  </div>

                  <AddChoiceForm optIndex={optIdx} onAddChoice={onAddOptionChoice} />
                </div>
              );
            })
          )}
        </Modal.Body>
        <Modal.Footer className="border-top-0 pt-0 px-4 pb-4">
          <Button variant="outline-secondary" onClick={onHide} style={{ borderRadius: '8px', fontSize: '13px' }}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" style={{ borderRadius: '8px', fontSize: '13px' }}>
            Save Pricing Configuration
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PricingModal;
